import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import AuthImage from "@/components/AuthImage";
import { ShieldCheck } from "lucide-react";
import { StillTruePill } from "@/components/StillTruePill";
import { GrowthStageGlyph } from "@/components/GrowthGlyphs";
import { cardExcerpt } from "@/lib/stripMarkdown";

const categoryTint = {
  Ideas: "bg-[#4E5A3D]/8 text-[#4E5A3D]",
  Recipes: "bg-[#B79256]/15 text-[#8B6E3F]",
  Lifestyle: "bg-[#2A2823]/8 text-[#2A2823]",
  Faith: "bg-[#4E5A3D]/15 text-[#3E4830]",
  Home: "bg-[#B79256]/12 text-[#7A5A2E]",
  Style: "bg-[#B24A2A]/10 text-[#B24A2A]",
  "Travel/Places": "bg-[#4E5A3D]/12 text-[#4E5A3D]",
  "Parenting/Family": "bg-[#B79256]/10 text-[#8B6E3F]",
  Wellness: "bg-[#4E5A3D]/10 text-[#4E5A3D]",
  Trusted: "bg-[#4E5A3D]/12 text-[#4E5A3D]",
};

/**
 * IdeaCard — a feed tile that surfaces a Seed. After the Feb 22 revision the
 * card is intentionally decoration-only: NO floating action buttons on the
 * image, NO raw counts in the meta row. All interactions (sun / regrow /
 * share / comment / save) live inside the opened seed on IdeaDetail. The
 * card only communicates identity + growth-stage word — everything else
 * happens once someone taps in.
 *
 * `savedIds` / `onSaveChange` / `hideRegrowByline` are still accepted as
 * props for backward compat with call sites (Roots, Grove) that used them
 * pre-revision; they're now no-ops but harmless.
 */
export default function IdeaCard({ idea, index = 0, hideRegrowByline = false }) {
  const [growthStage, setGrowthStage] = useState(idea.growth_stage || "sprout");
  const [growthLabel, setGrowthLabel] = useState(idea.growth_label || "sprout");

  // Keep growth stage in sync with what the feed passed in.
  useEffect(() => {
    if (idea.growth_stage) setGrowthStage(idea.growth_stage);
    if (idea.growth_label) setGrowthLabel(idea.growth_label);
  }, [idea.growth_stage, idea.growth_label]);

  return (
    <Link
      to={`/idea/${idea.id}`}
      data-testid={`idea-card-${idea.id}`}
      className="group block animate-fade-up relative"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
    >
        <div className="overflow-hidden rounded-3xl bg-white border border-[#B79256]/20 shadow-[0_10px_40px_rgb(42,40,35,0.06)] transition-transform duration-300 ease-out group-hover:-translate-y-1">
          <div className="relative w-full aspect-[4/5] bg-[#B79256]/5">
            {idea.photo_path ? (
              <AuthImage
                path={idea.photo_path}
                alt={idea.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            ) : (
              <div
                data-testid={`no-photo-${idea.id}`}
                className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#B79256]/12 to-[#4E5A3D]/8 text-center px-6"
              >
                <div className="w-14 h-14 rounded-full bg-[#B79256]/20 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-[#B79256]" strokeWidth={1.5} />
                </div>
                <span className="font-serif-display text-2xl text-[#2A2823] leading-tight">{idea.title}</span>
                {idea.provider_type && (
                  <span className="accent-label text-[#4E5A3D] mt-2">{idea.provider_type}</span>
                )}
                {idea.service_area && (
                  <span className="text-xs text-[#2A2823]/60 mt-1">{idea.service_area}</span>
                )}
              </div>
            )}
            {/* No floating icons on the feed card — per Feb 22 revision,
                the entire IG-style action row (sun / regrow / share /
                comment + save) lives INSIDE the opened seed on IdeaDetail.
                Cleaner card, more editorial feel. */}
          </div>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className={`accent-label px-3 py-1 rounded-full ${categoryTint[idea.category] || "bg-[#B79256]/10 text-[#8B6E3F]"}`}>
                {idea.category}
              </span>
              {idea.category === "Recipes" && idea.total_time && (
                <span className="accent-label text-[#2A2823]/60 inline-flex items-center gap-1">
                  · {idea.total_time}
                </span>
              )}
              <StillTruePill stillTrue={idea.still_true} size="sm" />
            </div>
            {/* Regrow byline — plain serif, no icons. Only shown when this
                feed entry surfaced because someone regrew the seed. The
                original author's identity/context is preserved below. */}
            {idea.is_regrow && idea.regrower_name && !hideRegrowByline && (
              <p
                data-testid={`regrow-byline-${idea.id}`}
                className="font-serif-display text-sm text-[#4E5A3D] italic mb-2 leading-snug break-words"
              >
                Regrown by{" "}
                <span
                  role="link"
                  tabIndex={0}
                  data-testid={`regrow-byline-name-${idea.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Manual navigation — nested <a> inside the card's outer
                    // <Link> is invalid HTML, so we route via window.location
                    // when the name is tapped. Feels the same to the user.
                    window.location.href = `/roots/${idea.regrower_id}`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      window.location.href = `/roots/${idea.regrower_id}`;
                    }
                  }}
                  className="cursor-pointer text-[#4E5A3D] underline decoration-[#B79256]/50 underline-offset-2 hover:decoration-[#4E5A3D]"
                >
                  {idea.regrower_name}
                </span>{" "}
                · Originally planted by {idea.author_name}
              </p>
            )}
            {idea.is_regrow && idea.regrower_note && (
              <p className="font-serif-display text-sm italic text-[#2A2823]/75 mb-2 leading-snug break-words">
                &ldquo;{idea.regrower_note}&rdquo;
              </p>
            )}
            <h3 className="font-serif-display text-2xl text-[#2A2823] leading-tight">{idea.title}</h3>
            {/* Card excerpt — prefer an author-written short caption
                (idea.summary); otherwise pull the first prose paragraph
                out of the Story markdown, skipping ingredient rows,
                headings, and other recipe scaffolding. Falls back to
                nothing (title-only) rather than dumping raw ingredients. */}
            {(() => {
              const excerpt = cardExcerpt(idea);
              return excerpt ? (
                <p className="text-sm text-[#2A2823]/60 mt-3 line-clamp-2">{excerpt}</p>
              ) : null;
            })()}
            <p className="text-xs text-[#2A2823]/50 mt-4 flex items-center gap-1.5 flex-wrap">
              <span>by <span className="text-[#4E5A3D]">{idea.author_name}</span></span>
              {/* Growth stage — only shown once a seed has moved past Sprout.
                  Per Feb 22 revision, raw counts (sun N, taken root N) are
                  hidden on the card by default; they surface only inside the
                  opened seed. Keeping just the stage word makes cards feel
                  more editorial and less like an engagement dashboard. */}
              {growthStage && growthStage !== "sprout" && (
                <>
                  <span aria-hidden="true">·</span>
                  <span
                    data-testid={`growth-stage-${idea.id}`}
                    className="inline-flex items-center gap-1 font-serif-display italic text-[#4E5A3D]"
                    title={`Growth stage: ${growthLabel}`}
                  >
                    <GrowthStageGlyph stage={growthStage} className="w-3.5 h-3.5" />
                    {growthLabel}
                  </span>
                </>
              )}
              {idea.created_at && (
                <>
                  <span aria-hidden="true">·</span>
                  <span
                    data-testid={`card-relative-${idea.id}`}
                    title={new Date(idea.created_at).toLocaleString()}
                  >
                    {(() => {
                      try {
                        return formatDistanceToNowStrict(new Date(idea.created_at), { addSuffix: true });
                      } catch {
                        return "";
                      }
                    })()}
                  </span>
                </>
              )}
              {idea.edited_at && (
                <>
                  <span aria-hidden="true">·</span>
                  <span className="italic text-[#2A2823]/40" title={`Edited ${new Date(idea.edited_at).toLocaleString()}`}>edited</span>
                </>
              )}
            </p>
          </div>
        </div>
    </Link>
  );
}
